import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import WysiwygEditor from "../../Components/Custom/DashlyDashboard/WysiwygEditor/WysiwygEditor";
import { DashlyTextInput } from "../../Components/inputs";
import { MEDIA_TYPE, MediaType, checkUploadableFiles, looksLikeVideoFile } from "../../Services/MediaAssetService";
import { MOVE_DIRECTION, MoveDirection, SECTION_TYPE, SectionType } from "../../Services/KnowledgeArticleSectionService";
import MediaDropZone from "./MediaDropZone";
import MediaUploadPreviewDialog from "./MediaUploadPreviewDialog";
// Reuses ArticleSectionEditor's own stylesheet — the DOM structure below is deliberately the same shape
// (same BEM classes) so a draft section looks identical to the real, persisted one it becomes after save.
import "./ArticleSectionEditor.scss";

export interface DraftMediaItem {
    localId: string;
    file: File;
    mediaType: MediaType;
    previewUrl: string;
}

export interface DraftSection {
    localId: string;
    sectionType: SectionType;
    title: string;
    showTitle: boolean;
    /** Only meaningful for a TEXT section. */
    content: string;
    /** IMAGE/VIDEO: at most one item (a new drop replaces it). SLIDESHOW: any number. Unused for TEXT. */
    media: DraftMediaItem[];
    /** TEXT only — inline body images inserted via the WYSIWYG editor's "insert image" button, keyed by the
     *  `data-pending-upload-id` marker WysiwygEditor embedded in `content` in place of a real asset uuid
     *  (there's no article/section to upload to yet). Resolved (uploaded, content rewritten) once this
     *  section is actually created — see KnowledgeArticleEditor#createDraftContent. A marker whose id isn't
     *  in this map any more (the user deleted the image from the text) is simply skipped, no cleanup needed. */
    pendingInlineImages: Record<string, File>;
}

const SECTION_TYPE_ICON: Record<string, string> = {
    [SECTION_TYPE.IMAGE]: "pi-image",
    [SECTION_TYPE.VIDEO]: "pi-video",
    [SECTION_TYPE.SLIDESHOW]: "pi-images",
    [SECTION_TYPE.TEXT]: "pi-align-left",
};

interface DraftSectionEditorProps {
    section: DraftSection;
    isFirst: boolean;
    isLast: boolean;
    onChange: (updates: Partial<DraftSection>) => void;
    onInlineImageInsert: (localId: string, file: File) => void;
    onMove: (direction: MoveDirection) => void;
    onDelete: () => void;
}

/**
 * A not-yet-persisted section while composing a brand-new article — everything (title, content, media
 * files) lives in the parent's draft state until the article itself is first saved. The parent also
 * autosaves that draft (metadata in secureLocalStorage, file bytes in IndexedDB) so a crash can restore
 * photos/videos too. After Save, KnowledgeArticleEditor creates the real section(s) and uploads every
 * staged file. This is the create-mode counterpart to ArticleSectionEditor.
 */
const DraftSectionEditor: React.FC<DraftSectionEditorProps> = ({ section, isFirst, isLast, onChange, onInlineImageInsert, onMove, onDelete }) => {
    const { t } = useTranslation();
    const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);

    const handleTitleBlur = () => {
        const trimmed = section.title.trim();
        if (trimmed) {
            onChange({ title: trimmed });
        }
    };

    const handleToggleShowTitle = () => onChange({ showTitle: !section.showTitle });

    /** An IMAGE or VIDEO section holds exactly that; a slideshow takes both, so there each file speaks for
     *  itself — same rule ArticleSectionEditor applies when it uploads. */
    const mediaTypeOf = (file: File): MediaType => {
        if (section.sectionType === SECTION_TYPE.IMAGE) {
            return MEDIA_TYPE.PHOTO;
        }
        if (section.sectionType === SECTION_TYPE.VIDEO) {
            return MEDIA_TYPE.VIDEO;
        }
        return looksLikeVideoFile(file) ? MEDIA_TYPE.VIDEO : MEDIA_TYPE.PHOTO;
    };

    const makeMediaItem = (file: File): DraftMediaItem => ({
        localId: crypto.randomUUID(),
        file,
        mediaType: mediaTypeOf(file),
        previewUrl: URL.createObjectURL(file),
    });

    /** A file over its size or duration cap is refused now, not when the article is saved. */
    const handleFilesSelected = async (files: File[]) => {
        const { accepted, violations } = await checkUploadableFiles(files);
        violations.forEach((violation) => toast.error(t(violation) as string));
        if (accepted.length > 0) {
            setPendingFiles(accepted);
        }
    };

    const handlePreviewConfirm = (finalFiles: File[]) => {
        setPendingFiles(null);
        const newItems = finalFiles.map(makeMediaItem);
        if (section.sectionType === SECTION_TYPE.SLIDESHOW) {
            onChange({ media: [...section.media, ...newItems] });
        } else {
            section.media.forEach((item) => URL.revokeObjectURL(item.previewUrl));
            onChange({ media: newItems.slice(0, 1) });
        }
    };

    const handleRemoveMedia = (localId: string) => {
        const target = section.media.find((item) => item.localId === localId);
        if (target) {
            URL.revokeObjectURL(target.previewUrl);
        }
        onChange({ media: section.media.filter((item) => item.localId !== localId) });
    };

    const renderBody = () => {
        if (section.sectionType === SECTION_TYPE.TEXT) {
            return (
                <WysiwygEditor
                    value={section.content}
                    editMode
                    onChange={(value) => onChange({ content: value })}
                    onLocalImageInsert={onInlineImageInsert}
                />
            );
        }

        if (section.sectionType === SECTION_TYPE.SLIDESHOW) {
            return (
                <div className="article-section-editor__slideshow">
                    {section.media.length > 0 && (
                        <div className="article-section-editor__slideshow-items">
                            {section.media.map((item) => (
                                <div key={item.localId} className="article-section-editor__slideshow-item">
                                    {item.mediaType === MEDIA_TYPE.VIDEO ? (
                                        <i className="pi pi-video" />
                                    ) : (
                                        <img src={item.previewUrl} alt="" />
                                    )}
                                    <button
                                        type="button"
                                        className="article-section-editor__slideshow-item-remove"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleRemoveMedia(item.localId);
                                        }}
                                    >
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <MediaDropZone
                        accept="image/*,video/*"
                        multiple
                        hint={t("knowledgeBase.form.dropSlideshowHint") as string}
                        onFilesSelected={handleFilesSelected}
                    />
                </div>
            );
        }

        // IMAGE or VIDEO — exactly one item, a new drop replaces it (mirrors ArticleSectionEditor).
        const accept = section.sectionType === SECTION_TYPE.IMAGE ? "image/*" : "video/*";
        const hint =
            section.sectionType === SECTION_TYPE.IMAGE
                ? (t("knowledgeBase.form.dropImageHint") as string)
                : (t("knowledgeBase.form.dropVideoHint") as string);
        const current = section.media[0];

        return (
            <div className="article-section-editor__single-media">
                <MediaDropZone
                    accept={accept}
                    hint={hint}
                    className="article-section-editor__media-drop"
                    onFilesSelected={handleFilesSelected}
                >
                    {current && (
                        <div className="article-section-editor__media-preview">
                            {section.sectionType === SECTION_TYPE.VIDEO ? (
                                // No real asset/signed URL exists yet for a draft item — just the local
                                // blob, so this plays it directly instead of going through
                                // DeferredVideoPlayer (which resolves signed renditions by asset uuid).
                                <video src={current.previewUrl} muted controls />
                            ) : (
                                <img src={current.previewUrl} alt="" />
                            )}
                            <span className="article-section-editor__media-replace">{t("knowledgeBase.form.replaceItem")}</span>
                        </div>
                    )}
                </MediaDropZone>
            </div>
        );
    };

    return (
        <div className="article-section-editor">
            <div className="article-section-editor__header">
                <i className={`pi ${SECTION_TYPE_ICON[section.sectionType]} article-section-editor__type-icon`} />
                <DashlyTextInput
                    className="article-section-editor__title-input"
                    value={section.title}
                    onChange={(e) => onChange({ title: e.target.value })}
                    onBlur={handleTitleBlur}
                />
                <div className="article-section-editor__header-actions">
                    <Button
                        type="button"
                        icon={section.showTitle ? "pi pi-eye" : "pi pi-eye-slash"}
                        text
                        rounded
                        title={t(section.showTitle ? "knowledgeBase.form.hideTitleOnPage" : "knowledgeBase.form.showTitleOnPage") as string}
                        onClick={handleToggleShowTitle}
                    />
                    <Button
                        type="button"
                        icon="pi pi-chevron-up"
                        text
                        rounded
                        disabled={isFirst}
                        title={t("knowledgeBase.form.moveSectionUp") as string}
                        onClick={() => onMove(MOVE_DIRECTION.UP)}
                    />
                    <Button
                        type="button"
                        icon="pi pi-chevron-down"
                        text
                        rounded
                        disabled={isLast}
                        title={t("knowledgeBase.form.moveSectionDown") as string}
                        onClick={() => onMove(MOVE_DIRECTION.DOWN)}
                    />
                    <Button
                        type="button"
                        icon="pi pi-trash"
                        text
                        rounded
                        title={t("knowledgeBase.form.deleteSection") as string}
                        onClick={onDelete}
                    />
                </div>
            </div>
            <div className="article-section-editor__body">{renderBody()}</div>
            {pendingFiles && (
                <MediaUploadPreviewDialog
                    files={pendingFiles}
                    onConfirm={handlePreviewConfirm}
                    onCancel={() => setPendingFiles(null)}
                />
            )}
        </div>
    );
};

export default DraftSectionEditor;
